import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { MousePointerClickIcon, HeadphonesIcon, SparklesIcon, FolderSearchIcon } from 'lucide-react';
const steps = [{
  icon: MousePointerClickIcon,
  index: '01',
  title: 'Start capture with one click',
  description: 'When a borrower call begins, you decide to hit capture. Nothing happens until you do — and a visible indicator shows whenever the app is listening.'
}, {
  icon: HeadphonesIcon,
  index: '02',
  title: 'Works with any dialer',
  description: "The app captures your microphone and the borrower's system audio. RingCentral, Dialpad, Five9, Zoom, Teams — if the call plays through your computer, it just works."
}, {
  icon: SparklesIcon,
  index: '03',
  title: 'AI writes mortgage-specific notes',
  description: 'Every call is transcribed and summarized into notes built for lending: goals, objections, income mentions, credit details, docs requested, and follow-ups.'
}, {
  icon: FolderSearchIcon,
  index: '04',
  title: 'Filed by borrower, searchable forever',
  description: "Everything lands in the borrower's profile with a timeline of past conversations — your private business call notes, ready whenever you need them."
}];
export function HowItWorks() {
  return <section id="how-it-works" className="relative overflow-hidden scroll-mt-16 border-t border-slate-100 texture-dots py-20 lg:py-28" data-id="element-288">
      <span aria-hidden="true" className="font-mono-brand pointer-events-none absolute -left-10 top-1/2 -translate-y-1/2 -rotate-90 text-[10rem] font-bold tracking-tighter text-slate-900/[0.04] lg:text-[14rem]" data-id="element-289">
        EXTRACT
      </span>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-290">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} className="mx-auto max-w-2xl text-center" data-id="element-291">
          <p className="font-mono-brand text-xs uppercase tracking-[0.2em] text-teal-600" data-id="element-292">
            // how_it_works
          </p>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl" data-id="element-293">
            From live call to borrower memory in four steps
          </h2>
        </motion.div>

        <div className="relative mt-16" data-id="element-294">
          {/* Animated connector line */}
          <motion.div initial={{
          scaleX: 0
        }} whileInView={{
          scaleX: 1
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 1.2,
          ease: 'easeInOut'
        }} className="absolute left-0 top-[22px] hidden h-px w-full origin-left bg-teal-600/30 lg:block" aria-hidden="true" data-id="element-295" />

          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-296">
            {steps.map((step, i) => <motion.div key={step.title} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: 0.3 + i * 0.15
          }} className="relative" data-id="element-297">
                <div className="flex items-center gap-3" data-id="element-298">
                  <span className="relative z-10 flex h-11 w-11 flex-none items-center justify-center rounded-lg border border-teal-600/30 bg-white shadow-sm" data-id="element-299">
                    <step.icon className="h-5 w-5 text-teal-600" aria-hidden="true" data-id="element-300" />
                  </span>
                  <span className="font-mono-brand text-xs font-bold tracking-widest text-slate-400" data-id="element-301">
                    {step.index} / 04
                  </span>
                </div>
                <h3 className="font-display mt-4 text-base font-bold text-slate-900" data-id="element-302">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-303">
                  {step.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}